// src/services/neo4jService.js
// This service handles the Neo4j database connection and query execution

class Neo4jService {
  constructor() {
    this.driver = null;
    this.neo4j = null; // Will be dynamically imported
  }

  async initialize(uri, username, password) {
    try {
      // Dynamically import neo4j-driver (must be installed via npm)
      const neo4jModule = await import('neo4j-driver');
      this.neo4j = neo4jModule.default;
      
      this.driver = this.neo4j.driver(
        uri, 
        this.neo4j.auth.basic(username, password),
        { maxConnectionPoolSize: 50 }
      );
      
      // Verify connection
      const session = this.driver.session();
      try {
        await session.run('RETURN 1 as test');
        console.log('Successfully connected to Neo4j database');
        return true;
      } finally {
        await session.close();
      }
    } catch (error) {
      console.error('Failed to initialize Neo4j connection:', error);
      return false;
    }
  }

  async runQuery(query, params = {}) {
    if (!this.driver) {
      throw new Error('Neo4j driver not initialized. Call initialize() first.');
    }

    // This logs the query and its parameters to the console before execution.
    console.groupCollapsed(`%c[Neo4j Query] %cExecuting...`, 'color: #888; font-weight: bold;', 'color: #1E90FF;');
    console.log("Query:", query);
    console.log("Parameters:", params);
    console.groupEnd();

    const session = this.driver.session();
    try {
      const result = await session.run(query, params);
      
      // Correctly process the result and store it in a variable first.
      const processedData = this.processResults(result);

      // Now, log the processed data that will be sent to the application.
      console.groupCollapsed(`%c[Neo4j Result] %cData returned`, 'color: #888; font-weight: bold;', 'color: #2E8B57;');
      console.log(processedData);
      console.groupEnd();

      // Finally, return the processed data.
      return processedData;

    } catch (error) {
      console.error('Query execution failed:', error);
      console.error('Failed Query:', query);
      console.error('Failed Parameters:', params);
      throw error;
    } finally {
      await session.close();
    }
  }

  processResults(result) {
    return result.records.map(record => {
      const processedRecord = {};
      record.keys.forEach(key => {
        // Process each field using the recursive helper
        processedRecord[key] = this.convertNeo4jTypes(record.get(key));
      });
      return processedRecord;
    });
  }

  convertNeo4jTypes(value) {
    // 1. Handle Neo4j Integers (Check using neo4j.isInt if driver provides it)
    // Use optional chaining for safety in case neo4j object isn't fully loaded yet
    if (this.neo4j?.isInt(value)) {
      // Safely convert to number, handling potential large integers
      // (Note: JS numbers might lose precision for very large 64-bit integers)
      return value.toNumber ? value.toNumber() : Number(value);
    }
    // 2. Handle Neo4j Date, Time, DateTime objects (check specific properties)
    // Check for Date object structure
    if (value && typeof value === 'object' && value.year !== undefined && value.month !== undefined && value.day !== undefined && value.hour === undefined) {
        try {
            // Format as YYYY-MM-DD string
            return `${String(value.year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;
        } catch (e) { /* Fall through */ }
    }
     // Check for DateTime object structure (add more checks if needed based on driver version)
     if (value && typeof value === 'object' && value.year !== undefined && value.month !== undefined && value.day !== undefined && value.hour !== undefined) {
         try {
             // Attempt to reconstruct an ISO-like string for JS Date parsing
             const isoString = `${String(value.year).padStart(4, '0')}-${String(value.month).padStart(2, '0')}-${String(value.day).padStart(2, '0')}T${String(value.hour).padStart(2, '0')}:${String(value.minute ?? 0).padStart(2, '0')}:${String(value.second ?? 0).padStart(2, '0')}.${String(value.nanosecond ?? 0).padStart(9, '0').substring(0,3)}Z`; // Add Z for UTC assumption
             const date = new Date(isoString);
             return !isNaN(date.getTime()) ? date.toISOString() : value; // Return ISO string or original object on failure
         } catch (e) { /* Fall through */ }
     }
    // 3. Handle Arrays: Recursively process each element
    if (Array.isArray(value)) {
      return value.map(element => this.convertNeo4jTypes(element));
    }
    // 4. Handle plain Objects (like Node properties or nested maps): Recursively process properties
    if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
      const processedObj = {};
      for (const key in value) {
        if (Object.hasOwnProperty.call(value, key)) {
          processedObj[key] = this.convertNeo4jTypes(value[key]);
        }
      }
      return processedObj;
    }
    // 5. Handle Neo4j Nodes and Relationships (Extract properties and recursively process them)
    if (value && typeof value === 'object') {
        if (value.constructor.name === 'Node' && value.properties) {
             return {
                 // Include standard node info if needed
                 // id: this.convertNeo4jTypes(value.identity),
                 // labels: value.labels,
                 // Recursively process properties
                 ...this.convertNeo4jTypes(value.properties) // Process the properties object
             };
        }
        if (value.constructor.name === 'Relationship' && value.properties) {
            return {
                 // Include standard relationship info if needed
                 // id: this.convertNeo4jTypes(value.identity),
                 // type: value.type,
                 // start: this.convertNeo4jTypes(value.start),
                 // end: this.convertNeo4jTypes(value.end),
                 // Recursively process properties
                 ...this.convertNeo4jTypes(value.properties) // Process the properties object
            };
        }
    }

    // 6. Return value as is if no conversion needed (string, boolean, null, standard number, etc.)
    return value;
  }


  // Helper method to transform graph data for visualization
  async getNetworkData(clientId) {
    const query = `
      MATCH (n {client_id: $clientId}) 
      WHERE n:Goal OR n:Initiative OR n:Issue OR n:ActionItem
      OPTIONAL MATCH (n)-[r]->(m {client_id: $clientId})
      WHERE type(r) IN ['SUPPORTS_GOAL', 'BLOCKS', 'SUPPORTS']
      RETURN n, r, m
    `;
    
    const result = await this.runQuery(query, { clientId });
    
    // Process into nodes and links for visualization
    const nodes = new Map();
    const links = [];
    
    result.forEach(record => {
      // Process source node
      if (record.n && !nodes.has(record.n.id)) {
        nodes.set(record.n.id, {
          id: record.n.id,
          label: record.n.name || 'Unnamed',
          type: record.n.labels[0],
          ...record.n
        });
      }
      
      // Process target node if relationship exists
      if (record.m && !nodes.has(record.m.id)) {
        nodes.set(record.m.id, {
          id: record.m.id,
          label: record.m.name || 'Unnamed',
          type: record.m.labels[0],
          ...record.m
        });
      }
      
      // Process relationship
      if (record.r) {
        links.push({
          source: record.r.start,
          target: record.r.end,
          type: record.r.type,
          ...record.r
        });
      }
    });
    
    return {
      nodes: Array.from(nodes.values()),
      links
    };
  }

  async getClientIds() {
    const query = 'MATCH (n) WHERE n.client_id IS NOT NULL RETURN DISTINCT n.client_id AS clientId ORDER BY clientId';
    const results = await this.runQuery(query);
    // Assuming results are like [{ clientId: 'LeanLaw' }, { clientId: 'ClientB' }]
    // You might need to adjust based on your actual client_id values
    return results.map(r => ({ id: r.clientId, name: r.clientId }));
  }

  close() {
    if (this.driver) {
      this.driver.close();
      this.driver = null;
    }
  }
}

// Export as singleton instance
const neo4jService = new Neo4jService();
export default neo4jService;